import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import Async from 'components/Async';
import DatePicker from 'components/DatePicker';
import DaysCalendar from 'components/DaysCalendar';
import Page from 'components/Page';
import ToolTip from 'components/Placeholder/ToolTip';
import { DateTime, IntervalObject } from 'luxon';
import AppointmentsVisualGuide from 'modules/Appointments/VisualGuide';
import store from 'store';

import { getMeetLessons } from '@common/api/meets';
import { checkRole } from '@common/components/RBAC';
import useDateQuery from '@common/helpers/useDateQuery';
import useModel from '@common/helpers/useModel';
import ReworkingMeetsModel, {
	IReworkingMeetsModel,
} from '@common/models/Appointments/ReworkingMeets';
import { IReworkingSlotModel } from '@common/models/Appointments/ReworkingSlot';
import ReworkingSlotsModel, {
	IReworkingSlotsModel,
} from '@common/models/Appointments/ReworkingSlots';
import { IGroupModel } from '@common/models/Groups/Group';
import LocationMeet from '@common/models/Locations/LocationMeet';
import { ITeacherModel } from '@common/models/Users/Teachers/Teacher';

import { ADDITIONAL_INFO_SECTION, INFO_SECTION, STEP_SECTION } from '../Academy/constants';
import { StyledAppointmentsHourWrap, StyledAppointmentsPageInnerWrap } from '../styled';
import AppointmentsBookingItem from './BookingItem';
import AppointmentsSelectPart from './SelectPart';
import VISUAL_GUIDE_STEPS from './constants';

type TFilters = {
	location_id?: number;
	group_id?: number;
	teacher_id?: number;
	program_id: number;
};

function AppointmentsPage() {
	const { t } = useTranslation();
	const { date, dateTime, setDateTime } = useDateQuery();
	const [loading, setLoading] = useState(true);
	const [programs, setPrograms] = useState([]);

	const defaultLocation = store.profile.serviceLocations[0];
	const [filters, setFilters] = useState<TFilters>({
		location_id: defaultLocation?.id,
		group_id: store.student.groups.filter(
			(group: IGroupModel) => group.location.id === defaultLocation?.id,
		)[0]?.id,
		program_id: 0,
	});

	useEffect(() => {
		const loadLessons = async (studentId: number, groupId: number) => {
			try {
				setLoading(true);
				const result = await getMeetLessons(studentId, groupId);
				setPrograms([
					{
						label: t('shared.schoolAdministration'),
						value: 0,
						program_group_id: null,
					},
					...result.programs.map((program: any) => ({
						label: program.program_name,
						value: program.program_id,
						program_group_id: program.program_group_id,
						teachers: program.teacher ? [program.teacher] : [],
					})),
				]);
			} finally {
				setLoading(false);
			}
		};
		loadLessons(store.student.id, filters.group_id);
	}, [filters.group_id]);

	const selectedProgram = programs.find((program) => program.value === filters.program_id);

	const locationMeet = useModel(
		LocationMeet,
		{
			id: filters.location_id,
		},
		[filters.location_id],
		{
			disableFetch: true,
		},
	);

	useEffect(() => {
		locationMeet.fetch({
			type: 'appointment',
		});
	}, [filters.location_id]);

	const teacher = (
		selectedProgram?.program_group_id ? selectedProgram.teachers : locationMeet.teachers
	).find((teacher: ITeacherModel) => teacher.id === filters.teacher_id);

	const reworkingSlots = useModel(
		ReworkingSlotsModel,
		{
			period_start: dateTime.startOf('week').toISODate(),
			period_end: dateTime.endOf('week').toISODate(),
			location_id: locationMeet.id,
			service_id: store.services.current.id,
			...(filters.teacher_id && { teacher_id: filters.teacher_id }),
			...(filters.group_id && { group_id: filters.group_id }),
			type: 'appointment',
			slotDuration: locationMeet.duration,
		},
		[locationMeet, date, filters.teacher_id],
	) as IReworkingSlotsModel;

	const reworkingMeets = useModel(
		ReworkingMeetsModel,
		{
			period_start: dateTime.startOf('week').toISODate(),
			period_end: dateTime.endOf('week').toISODate(),
			location_id: locationMeet.id,
			service_id: store.services.current.id,
			...(filters.teacher_id && { teacher_id: filters.teacher_id }),
			...(filters.group_id && { group_id: filters.group_id }),
			slotDuration: locationMeet.duration,
		},
		[locationMeet, date, filters.teacher_id],
	) as IReworkingMeetsModel;

	const renderHour = (hour: number, day: IntervalObject) => {
		const hourDateTime = DateTime.fromObject({ hour, minute: 0 });
		const slots = reworkingSlots.daysMap[day.start.weekday][hour];

		return (
			<StyledAppointmentsHourWrap>
				<Typography variant="caption2">{hourDateTime.toFormat('HH:mm')}</Typography>
				{/* eslint-disable-next-line no-confusing-arrow */}
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '4px' }}>
					{Object.values(slots).map((slot: IReworkingSlotModel) =>
						slot ? (
							<AppointmentsBookingItem
								key={slot.id}
								slot={slot}
								reworkingMeets={reworkingMeets}
								teacher={teacher}
								locationMeet={locationMeet}
								onSave={async (values) => {
									await reworkingMeets.addMeet({
										meet_slot_id: slot.id,
										teacher_id: teacher.id,
										...(checkRole('Parent') && {
											parent_id: store.profile.parent?.id,
										}),
										student_id: store.student.id,
										...(selectedProgram?.program_group_id && {
											program_group_id: selectedProgram.program_group_id,
										}),
										...values,
									});
									slot.setStatus(
										1,
										store.student.id,
										checkRole('Parent') && store.profile.parent?.id,
									);
								}}
							/>
						) : null,
					)}
				</Box>
			</StyledAppointmentsHourWrap>
		);
	};

	return (
		<Page
			type="list"
			title={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{t('appointment.lyceum')}
					<ToolTip title={t('appointment.timeZone')} placement="right">
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<InfoOutlinedIcon />
						</Box>
					</ToolTip>
				</Box>
			}
			sx={{ overflow: 'hidden' }}
			header={<DatePicker value={dateTime} onChange={setDateTime} format="day" />}
			isLoading={loading}
		>
			<StyledAppointmentsPageInnerWrap>
				<AppointmentsSelectPart
					filters={filters}
					teachers={
						selectedProgram?.program_group_id ? selectedProgram.teachers : locationMeet.teachers
					}
					onSubmit={setFilters}
					programs={programs}
				/>
				{teacher && filters.group_id ? (
					<Async loading={reworkingSlots.loading || reworkingMeets.loading}>
						<DaysCalendar
							date={date}
							dateTime={dateTime}
							days={reworkingSlots.days}
							renderHour={renderHour}
							minHour={reworkingSlots.minHour}
							maxHour={reworkingSlots.maxHour}
						/>
					</Async>
				) : (
					<AppointmentsVisualGuide
						infoSection={INFO_SECTION}
						bookingSteps={STEP_SECTION}
						visualGuideSteps={VISUAL_GUIDE_STEPS}
						additionalInfoSection={ADDITIONAL_INFO_SECTION}
						styleProps={{
							borderColor: '#0A336F',
							titlesColor: '#292929',
							boxShadowStyle: '0px 0px 30px 0px rgba(130, 125, 131, 0.15)',
							sectionDescriptionImage: false,
							usefulInfoImage: false,
						}}
					/>
				)}
			</StyledAppointmentsPageInnerWrap>
		</Page>
	);
}

export default observer(AppointmentsPage);
